<script>
export default {
  name: 'CpTruncateText',
  components: {
    CpDialog: require('../dialog.vue').default,
  },
  directives: {
    marginBottom: require('../directives/margins').marginBottom,
  },
  props: {
    content: { type: String, default: '' },
    enableTruncate: { type: Boolean, default: true },
    lineClamp: { type: Number, default: 3 },
    moreButtonInSameLine: { type: Boolean, default: false },
    withMoreButton: { type: Boolean, default: true },
    textClass: { type: String, default: 'body black-color' },
  },
  data() {
    return {
      isTruncated: true,
      width: 0,
      showVideoModal: false,
      modalVideoUrl: '',
      modalTitle: 'Video player',
    };
  },
  mounted() {
    const textElement = this.$refs.textContent;
    if (textElement) {
      this.width = textElement.getBoundingClientRect().width;
    }
  },
  computed: {
    embedUrl() {
      return this.modalVideoUrl ? this.getEmbedUrl(this.modalVideoUrl) : null;
    },
    isEmbed() {
      return this.embedUrl !== null;
    },
    isDirectVideo() {
      return this.modalVideoUrl ? this.isDirectVideoUrl(this.modalVideoUrl) : false;
    },
    socialName() {
      return this.modalVideoUrl ? this.socialMediaName(this.modalVideoUrl) : '';
    },
    isVertical() {
      const isInstagram = this.socialName === 'Instagram';
      const isTikTok = this.socialName === 'TikTok';
      const isFacebookReel = this.socialName === 'Facebook' && this.modalVideoUrl.includes('reel/');
      return isInstagram || isTikTok || isFacebookReel;
    },
    modalVideoSrc() {
      return this.modalVideoUrl;
    },
    numberOfLines() {
      if (!this.width) {
        return 0
      };
      const textElement = this.$refs.textContent;
      if (!textElement) {
        return 0
      };

      const computedStyle = window.getComputedStyle(textElement);
      const lineHeight = parseFloat(computedStyle.lineHeight);
      const textHeight = textElement.scrollHeight;

      if (this.isHTMLContent(this.content)) {
        return this.calculateHeightForHTML(textElement, lineHeight);
      } else {
        return Math.round(textHeight / lineHeight);
      }
    }
  },
  methods: {
    openVideoModal(videoUrl) {
      if (!videoUrl) return;
      this.modalVideoUrl = videoUrl;
      this.showVideoModal = true;
    },
    closeVideoModal() {
      this.showVideoModal = false;
      this.modalVideoUrl = '';
    },
    getVideoUrlFromWrapper(wrapper) {
      const iframe = wrapper.querySelector('iframe');
      if (iframe) {
        return iframe.getAttribute('data-url') || iframe.getAttribute('src') || '';
      }

      const source = wrapper.querySelector('video source');
      if (source) {
        return source.getAttribute('src') || '';
      }

      const video = wrapper.querySelector('video');
      if (video) {
        return video.getAttribute('src') || '';
      }

      return '';
    },
    handleTextContainerClick(event) {
      const target = event.target;
      if (!target || !target.closest) return;

      // Avoid interfering with the "(more)" toggle.
      const moreButton = target.closest('.more-button');
      if (moreButton) return;

      const wrapper = target.closest('.video-container, .video-fb-reel-container, .video-meta-container');
      if (!wrapper) return;

      const videoUrl = this.getVideoUrlFromWrapper(wrapper);
      if (!videoUrl) return;

      this.openVideoModal(videoUrl);
    },
    isHTMLContent(content) {
      const div = document.createElement('div');
      div.innerHTML = content;
      return div.getElementsByTagName('*').length > 0;
    },
    calculateHeightForHTML(textElement, lineHeight) {
      const textHeight = textElement.scrollHeight;
      const clientHeight = textElement.clientHeight;
      const lines = Math.round(textHeight / lineHeight);

      if (textHeight > clientHeight) {
        const extraLines = Math.floor(textHeight / lineHeight);
        return lines + extraLines;
      } else {
        return 0
      }
    },
    handleTruncateContent() {
      this.isTruncated = !this.isTruncated;
    },
  },
};
</script>
<template>
  <div class="text-container" :class="{ 'more-button-in-same-line': moreButtonInSameLine }" @click="handleTextContainerClick">
    <span
      ref="textContent"
      :class="[textClass, { 'truncate-text': enableTruncate && isTruncated }]"
      :style="isTruncated ? { '-webkit-line-clamp': lineClamp } : {}"
      v-html="content"
      v-margin-bottom="0"
    />
    <span v-if="withMoreButton && enableTruncate && isTruncated && numberOfLines > lineClamp" @click="handleTruncateContent" class="body primary-color more-button"> (more) </span>
    <CpDialog v-if="showVideoModal" withoutScrolling @dismiss="closeVideoModal">
      <div class="cpVideoPreviewModal__modal" role="document">
        <button type="button" class="cpVideoPreviewModal__closeButton" aria-label="Close video" @click="closeVideoModal">
          <i class="fal fa-times"></i>
        </button>

        <div class="cpVideoPreviewModal__modalMedia">
          <div
            class="cpVideoPreviewModal__modalFrame"
            :class="{ 'cpVideoPreviewModal__modalFrame--vertical': isVertical }"
          >
            <template v-if="isEmbed">
              <iframe
                class="cpVideoPreviewModal__modalIframe"
                :src="embedUrl"
                frameborder="0"
                allowfullscreen="allowfullscreen"
                :title="modalTitle"
              />
            </template>

            <template v-else-if="isDirectVideo">
              <video class="cpVideoPreviewModal__modalVideo" controls autoplay playsinline preload="metadata">
                <source :src="modalVideoSrc" type="video/mp4" />
              </video>
            </template>
          </div>
        </div>
      </div>
    </CpDialog>
  </div>
</template>

<style scoped>
.truncate-text {
  display: -webkit-box;
  line-clamp: var(--line-clamp, 3);
  -webkit-line-clamp: var(--line-clamp, 3);
  -webkit-box-orient: vertical;
  -webkit-box-pack: end;
  text-overflow: ellipsis;
  overflow: hidden;
}
.text-container {
  overflow: hidden;
}
.text-container.more-button-in-same-line {
  display: flex;
  align-items: center;
}
.more-button {
  cursor: pointer;
}
.text-container >>> figure,
.text-container ::v-deep figure {
  max-width: 50%;
  margin: 0.5em auto;
  display: block;
}

.text-container >>> figure img,
.text-container ::v-deep figure img {
  width: 100%;
  height: auto;
  display: block;
}
.text-container >>> .video-container,
.text-container >>> .video-fb-reel-container,
.text-container >>> .video-meta-container {
  /* Match visual preview modal */
  position: relative;
  width: 100%;
  max-height: var(--cp-video-preview-max-height, 350px);
  overflow: hidden;
  border-radius: var(--external-border-radius);
  background: #000;
  display: flex;
  justify-content: center;
  cursor: pointer;
}
.text-container >>> .video-container {
  aspect-ratio: 16 / 9;
}
.text-container >>> .video-container:not(:has(iframe)),
.text-container >>> .video-fb-reel-container:not(:has(iframe)),
.text-container >>> .video-meta-container:not(:has(iframe)) {
  display: none;
}
.text-container >>> .video-fb-reel-container {
  aspect-ratio: 9 / 16;
}
.text-container >>> .video-meta-container {
  aspect-ratio: 16 / 9;
}
.text-container >>> .video-container iframe,
.text-container >>> .video-fb-reel-container iframe,
.text-container >>> .video-meta-container iframe {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}
.text-container >>> .video-container video,
.text-container >>> .video-fb-reel-container video,
.text-container >>> .video-meta-container video {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
  object-fit: cover;
}
.text-container >>> .video-container::after,
.text-container >>> .video-fb-reel-container::after,
.text-container >>> .video-meta-container::after {
  /* Click-catcher overlay (important for cross-origin iframes) */
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0);
  z-index: 1;
}
.text-container >>> .video-container::before,
.text-container >>> .video-fb-reel-container::before,
.text-container >>> .video-meta-container::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  width: 0;
  height: 0;
  border-top: 12px solid transparent;
  border-bottom: 12px solid transparent;
  border-left: 18px solid rgba(255, 255, 255, 0.95);
}
.text-container >>> .video-container iframe,
.text-container >>> .video-fb-reel-container iframe,
.text-container >>> .video-meta-container iframe,
.text-container >>> .video-container video,
.text-container >>> .video-fb-reel-container video,
.text-container >>> .video-meta-container video {
  position: relative;
  z-index: 0;
}
@media (max-width: 1000px) {
  .text-container >>> figure,
  .text-container ::v-deep figure {
    max-width: 75%;
  }
}
@media (max-width: 768px) {
  .text-container >>> figure,
  .text-container ::v-deep figure {
    max-width: 100%;
  }

  .text-container >>> .video-container,
  .text-container >>> .video-fb-reel-container,
  .text-container >>> .video-meta-container {
    max-height: min(var(--cp-video-preview-max-height, 350px), 250px);
  }
}

@media (max-width: 500px) {
  .text-container >>> .video-container,
  .text-container >>> .video-fb-reel-container,
  .text-container >>> .video-meta-container {
    max-height: min(var(--cp-video-preview-max-height, 350px), 150px);
  }
}

/* Modal (copied from VideoPreviewModal.vue) */
.cpVideoPreviewModal__modal {
  width: 100%;
  height: auto;
  max-height: 100%;
  background: #000;
  display: grid;
  grid-template-rows: auto 1fr;
  position: relative;
  overflow: hidden;
}

.cpVideoPreviewModal__closeButton {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  width: 44px;
  height: 44px;
  border-radius: 999px;
  cursor: pointer;
}

.cpVideoPreviewModal__closeButton:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 3px;
}

.cpVideoPreviewModal__modalMedia {
  width: 100%;
  height: auto;
  display: grid;
  place-items: center;
  padding: 20px;
  box-sizing: border-box;
}

.cpVideoPreviewModal__modalFrame {
  position: relative;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  /* Drive the height from the limiting viewport constraints.
     This prevents external letterboxing/cropping when width != height. */
  height: min(
    82vh,
    82dvh,
    max(0px, calc(90vh - 40px)),
    max(0px, calc(90dvh - 40px)),
    calc(min(960px, 85vw) * 9 / 16)
  );
}

.cpVideoPreviewModal__modalFrame--vertical {
  aspect-ratio: 9 / 16;
  height: min(
    82vh,
    82dvh,
    max(0px, calc(90vh - 40px)),
    max(0px, calc(90dvh - 40px)),
    calc(min(520px, 92vw) * 16 / 9)
  );
}

.cpVideoPreviewModal__modalIframe,
.cpVideoPreviewModal__modalVideo {
  border: 0;
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.cpVideoPreviewModal__modalVideo {
  object-fit: cover;
}

@media (max-width: 768px) {
  .cpVideoPreviewModal__modalMedia {
    padding: 8px;
  }
}
</style>
